import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Slider from '@mui/material/Slider';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Cropper, { Area } from 'react-easy-crop';
import { useTranslation } from 'react-i18next';
import AlertBlock from './AlertBlock';
import { useAuthenticatedImage } from '../hooks/useAuthenticatedImage';

const MAX_EDGE = 1600;
const JPEG_QUALITY = 0.85;

type AspectPresetKey = 'original' | '1:1' | '4:3' | '16:9';

const ASPECT_PRESET_RATIOS: Record<Exclude<AspectPresetKey, 'original'>, number> = {
  '1:1': 1,
  '4:3': 4 / 3,
  '16:9': 16 / 9,
};

const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('read_failed'));
    reader.onload = () => resolve(reader.result as string);
    reader.readAsDataURL(file);
  });

const loadImage = (source: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onerror = () => reject(new Error('decode_failed'));
    img.onload = () => resolve(img);
    img.src = source;
  });

const cropAndDownscaleToJpeg = (source: string, area: Area): Promise<string> =>
  new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onerror = () => reject(new Error('decode_failed'));
    img.onload = () => {
      const scale = Math.min(1, MAX_EDGE / Math.max(area.width, area.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(area.width * scale);
      canvas.height = Math.round(area.height * scale);

      const ctx = canvas.getContext('2d');
      if (!ctx || canvas.width === 0 || canvas.height === 0) {
        reject(new Error('canvas_failed'));
        return;
      }

      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', JPEG_QUALITY));
    };
    img.src = source;
  });

interface IEntityImageCaptureProps {
  imageUrl?: string | null;
  canEdit: boolean;
  placeholderIconClass: string;
  widthOnDesktop: string;
  onUpload: (dataUrl: string) => Promise<unknown>;
  altText?: string;
  imageMaxHeight?: number;
  imageObjectFit?: React.CSSProperties['objectFit'];
}

const EntityImageCapture: React.FC<IEntityImageCaptureProps> = ({
  imageUrl,
  canEdit,
  placeholderIconClass,
  widthOnDesktop,
  onUpload,
  altText,
  imageMaxHeight,
  imageObjectFit,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('md'));
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cropSource, setCropSource] = useState<string | null>(null);
  const [sourceAspect, setSourceAspect] = useState(1);
  const [aspectPreset, setAspectPreset] = useState<AspectPresetKey>('original');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const photoSrc = useAuthenticatedImage(imageUrl);
  const cropContainerRef = useRef<HTMLDivElement>(null);
  const [containerAspect, setContainerAspect] = useState<number | null>(null);

  const aspect = aspectPreset === 'original' ? sourceAspect : ASPECT_PRESET_RATIOS[aspectPreset];
  const objectFit: 'horizontal-cover' | 'vertical-cover' | null =
    containerAspect === null ? null : sourceAspect > containerAspect ? 'horizontal-cover' : 'vertical-cover';

  const measureCropContainer = useCallback(() => {
    const el = cropContainerRef.current;
    if (!el) {
      return false;
    }
    const { width, height } = el.getBoundingClientRect();
    if (width <= 0 || height <= 0) {
      return false;
    }
    setContainerAspect(width / height);
    return true;
  }, []);

  useEffect(() => {
    if (!cropSource) {
      setContainerAspect(null);
      return undefined;
    }
    if (measureCropContainer()) {
      return undefined;
    }
    let rafId = 0;
    const retry = () => {
      if (!measureCropContainer()) {
        rafId = requestAnimationFrame(retry);
      }
    };
    rafId = requestAnimationFrame(retry);
    return () => cancelAnimationFrame(rafId);
  }, [cropSource, measureCropContainer]);

  useEffect(() => {
    if (!cropSource) {
      return undefined;
    }
    const handleResize = () => measureCropContainer();
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [cropSource, measureCropContainer]);

  const onCropComplete = useCallback((_: Area, areaPixels: Area) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleAspectPresetChange = (_: React.MouseEvent<HTMLElement>, value: AspectPresetKey | null) => {
    if (value === null) {
      return;
    }
    setAspectPreset(value);
    setCrop({ x: 0, y: 0 });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) {
      return;
    }

    setError(null);
    try {
      const dataUrl = await readFileAsDataUrl(file);
      const img = await loadImage(dataUrl);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setSourceAspect(img.width / img.height);
      setAspectPreset('original');
      setCropSource(dataUrl);
    } catch {
      setError(t('entity_image.upload_failed'));
    }
  };

  const handleCancel = () => setCropSource(null);

  const handleConfirm = async () => {
    if (!cropSource || !croppedAreaPixels) {
      return;
    }

    setError(null);
    setBusy(true);
    try {
      const dataUrl = await cropAndDownscaleToJpeg(cropSource, croppedAreaPixels);
      await onUpload(dataUrl);
      setCropSource(null);
    } catch {
      setError(t('entity_image.upload_failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ width: isXs ? '100%' : widthOnDesktop, marginTop: '10px' }}>
      <Box sx={{ position: 'relative' }}>
        {imageUrl ? (
          photoSrc ? (
            <img
              src={photoSrc}
              alt={altText ?? ''}
              style={{
                width: '100%',
                height: imageMaxHeight ? `${imageMaxHeight}px` : 'auto',
                objectFit: imageObjectFit,
                borderRadius: 8,
              }}
            />
          ) : (
            <Box
              sx={{
                minHeight: 200,
                backgroundColor: '#f0f0f0',
                borderRadius: 2,
              }}
            />
          )
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              minHeight: 200,
              backgroundColor: '#f0f0f0',
              borderRadius: 2,
              color: '#b0b0b0',
            }}
          >
            <i className={placeholderIconClass} style={{ fontSize: '64px' }} />
            {canEdit && (
              <Typography variant='body2' sx={{ color: '#8f8f8f', textAlign: 'center' }}>
                {t('entity_image.add')}
              </Typography>
            )}
          </Box>
        )}

        {canEdit && (
          <IconButton
            aria-label={imageUrl ? t('entity_image.replace') : t('entity_image.add')}
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              width: 40,
              height: 40,
              color: '#212121',
              backgroundColor: 'rgba(255, 255, 255, 0.85)',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.95)' },
            }}
          >
            {busy ? <CircularProgress size={18} /> : <i className='fas fa-camera' />}
          </IconButton>
        )}

        <input
          ref={fileInputRef}
          type='file'
          accept='image/*'
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </Box>

      {error && !cropSource && <AlertBlock type='error'>{error}</AlertBlock>}

      <Dialog open={Boolean(cropSource)} onClose={busy ? undefined : handleCancel} fullWidth maxWidth='xs'>
        <DialogTitle>{t('entity_image.confirm_title')}</DialogTitle>
        <DialogContent>
          {error && <AlertBlock type='error'>{error}</AlertBlock>}
          <Box ref={cropContainerRef} sx={{ position: 'relative', width: '100%', height: 320, backgroundColor: '#000' }}>
            {cropSource && objectFit && (
              <Cropper
                key={objectFit}
                image={cropSource}
                crop={crop}
                zoom={zoom}
                aspect={aspect}
                objectFit={objectFit}
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            )}
          </Box>
          <ToggleButtonGroup
            value={aspectPreset}
            exclusive
            size='small'
            onChange={handleAspectPresetChange}
            disabled={busy}
            sx={{ mt: 2, width: '100%' }}
          >
            <ToggleButton value='original' sx={{ flex: 1, minWidth: 0, px: 0.5 }}>
              {t('entity_image.aspect_original')}
            </ToggleButton>
            <ToggleButton value='1:1' sx={{ flex: 1, minWidth: 0, px: 0.5 }}>
              1:1
            </ToggleButton>
            <ToggleButton value='4:3' sx={{ flex: 1, minWidth: 0, px: 0.5 }}>
              4:3
            </ToggleButton>
            <ToggleButton value='16:9' sx={{ flex: 1, minWidth: 0, px: 0.5 }}>
              16:9
            </ToggleButton>
          </ToggleButtonGroup>
          <Box sx={{ mt: 2 }}>
            <Slider
              value={zoom}
              min={1}
              max={3}
              step={0.01}
              onChange={(_, value) => setZoom(value as number)}
              disabled={busy}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} disabled={busy}>
            {t('entity_image.cancel')}
          </Button>
          <Button variant='contained' onClick={handleConfirm} disabled={busy || !croppedAreaPixels}>
            {busy ? (
              <CircularProgress size={16} />
            ) : imageUrl ? (
              t('entity_image.replace')
            ) : (
              t('entity_image.add')
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EntityImageCapture;
